import {IncomingMessage, ServerResponse} from 'http';
import {END} from 'redux-saga';
import {SITE_NAME} from './component/Meta';
import {
  KeyboardKeyType,
  IS_CLIENT,
  UNKNOWN,
  REGION,
  IS_CBT_OR_PROD,
  IS_QA,
  THEME_DATASET_KEY,
  DEFAULT_THEME,
} from './constant';
import {Page} from './constant/page';
import {actions} from './state/action';
import {getClientStore} from './store';
import {
  PStore,
  IFetchInfoItem,
  IFetchStatus,
  IUserAgent,
  ITheme,
  IRegion,
  IBadge,
} from './type';
import {assert} from './util/assert';
import {getCookie, setCookie} from './util/cookie';
import {getUrl, PageInfo, redirectTo} from './util/PageRouter';
import {addRouterObserver} from './util/routerObserver';
import {getLocalStorageString} from './util/storage';
import {getIsWebView} from './util/userAgent';
import {RETURN_PAGE_INFO_LS_KEY, saveReturnPage} from '../auth/common';
import {actions as authActions} from '../auth/state/action';
import {IIdVerificationModalType, IAuthModalTitleType} from '../auth/type';

export function logOnServer({
  msg,
  req,
  isError,
  err,
}: {
  msg: string;
  req?: IncomingMessage;
  isError?: boolean;
  err?: Error | null;
}) {
  if (req) {
    if (isError) {
      (req as any).pageParam.winston.error(msg, err);
    } else {
      (req as any).pageParam.winston.info(msg);
    }
  }
}

export function getKeyboardKeyType(e: KeyboardEventObject) {
  /**
   * key 는 크롬 하위 버전에서 지원이 안되기 때문에 keyCode 도 같이 봐야한다
   * keyCode 만 봐도 되겠지만 deprecated 된 속성값이라 key 랑 같이 보는게 좋다
   */
  const key = e.key || e.keyCode;
  switch (key) {
    case 'ArrowLeft':
    case 'Left':
    case 37:
      return KeyboardKeyType.ArrowLeft;
    case 'ArrowRight':
    case 'Right':
    case 39:
      return KeyboardKeyType.ArrowRight;
    case 'ArrowUp':
    case 'Up':
    case 38:
      return KeyboardKeyType.ArrowUp;
    case 'ArrowDown':
    case 'Down':
    case 40:
      return KeyboardKeyType.ArrowDown;
    case ' ':
    case 'Spacebar':
    case 32:
      return KeyboardKeyType.Space;
    case 'Escape':
    case 'Esc':
    case 27:
      return KeyboardKeyType.Escape;
    case 'Enter':
    case 13:
      return KeyboardKeyType.Enter;
    case 33:
      return KeyboardKeyType.PageUp;
    case 34:
      return KeyboardKeyType.PageDown;
    default:
      return KeyboardKeyType.None;
  }
}

export function openWindow({
  url,
  windowName,
  features = '',
}: {
  url: string;
  windowName: string;
  features?: string;
}) {
  assert(IS_CLIENT, '클라이언트에서 실행되어야합니다.');
  const popupFeatures = `toolbar=no, directories=no, scrollbars=yes, resizable=no, status=no, menubar=no, location=no${
    features && ', '
  }${features}`;
  return window.open(url, windowName, popupFeatures);
}

export function getIsFetching(fetchStatusMap: IFetchInfoItem<IFetchStatus>) {
  return (
    Object.values(fetchStatusMap).findIndex(item =>
      Object.values(item).includes('request')
    ) >= 0
  );
}

export async function waitSaga(pStore: PStore) {
  if (pStore.sagaTask) {
    if (IS_CLIENT) {
      await new Promise<void>(resolve => {
        function checkIsFetching() {
          if (pStore.sagaTask) {
            const fetchStatusMap =
              pStore.getState().common.fetchInfo.fetchStatusMap;
            const isFetching = getIsFetching(fetchStatusMap);
            if (isFetching) {
              requestAnimationFrame(checkIsFetching);
            } else {
              resolve();
            }
          }
        }
        requestAnimationFrame(checkIsFetching);
      });
    } else {
      pStore.dispatch(END);
      await pStore.sagaTask.toPromise();
    }
  }
}

export function getLoginReturnPageInfo() {
  const returnPageInfo = getLocalStorageString({
    key: RETURN_PAGE_INFO_LS_KEY,
  });
  return returnPageInfo ? returnPageInfo : getUrl({page: Page.Home});
}

export function getHtmlWithCdnUrl(html: string, cdn: string) {
  const cdnWithSlash = cdn.substr(-1) !== '/' ? `${cdn}/` : cdn;
  return html.replace(getRegexStaticWeb(), `${cdnWithSlash}$&`);
}

export function copyToClipboard({
  copyText,
  onSuccess,
  onError,
}: {
  copyText: string;
  onSuccess?: () => void;
  onError?: () => void;
}) {
  const textArea = document.createElement('textarea');
  textArea.readOnly = true;
  textArea.value = copyText;

  document.body.appendChild(textArea);
  textArea.select();
  textArea.setSelectionRange(0, copyText.length);

  if (document.execCommand('copy')) {
    onSuccess && onSuccess();
  } else {
    onError && onError();
  }
  document.body.removeChild(textArea);
}

export function submitFormAction({
  url,
  method = 'post',
  params,
}: {
  url: string;
  method?: 'post' | 'get';
  params: Array<{name: string; value: string}>;
}) {
  const form = document.createElement('form');
  form.setAttribute('action', url);
  form.setAttribute('method', method);
  params.forEach(item => {
    const input = document.createElement('input');
    input.setAttribute('type', 'hidden');
    input.setAttribute('readonly', 'readonly');
    input.setAttribute('name', item.name);
    input.setAttribute('value', item.value);
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
}

export function callIfFunc(funcVal: any, ...param: any[]) {
  return typeof funcVal === 'function' ? funcVal(...param) : funcVal;
}

// 참고링크: https://github.com/tj/co/blob/717b043371ba057cb7a4a2a4e47120d598116ed7/index.js#L221
export function getIsGeneratorFunction(obj: object) {
  const constructor = obj.constructor;
  if (!constructor) {
    return false;
  }
  if (
    'GeneratorFunction' === constructor.name ||
    'GeneratorFunction' === (constructor as any).displayName
  ) {
    return true;
  }
  const proto = constructor.prototype;
  return 'function' === typeof proto.next && 'function' === typeof proto.throw;
}

export function getIsVerticalEnd(offsetHeight?: number) {
  assert(IS_CLIENT, '클라이언트에서 실행되어야합니다.');

  // 경우에 따라 1픽셀 이하의 차이가 발생해서 여유있게 하단 10픽셀을 끝으로 판단한다.
  return (
    window.scrollY + window.innerHeight >=
    (offsetHeight ?? document.body.offsetHeight) - 10
  );
}

export function getDeviceId() {
  return getClientId();
}
export function getDeviceName(userAgent?: IUserAgent, appDeviceName?: string) {
  if (!userAgent) {
    return UNKNOWN;
  }
  const {browserName, version, osName, osVersion} = userAgent;
  // 웹뷰일때는 항상 appDeviceName가 존재하지만, 만약 없을 때의 대비해서 osName.osVersion을 사용한다.
  return getIsWebView()
    ? appDeviceName || `${osName}.${osVersion}`
    : `${browserName}.${version}`;
}

const COOKIE_DEVICE_ID_KEY = '_kp_collector';
const TWO_YEARS_BY_HOURS = 2 * 365 * 24;
// 멤버 API에서 deviceId를 사용해 BI 데이터를 전송하기 때문에 BI를 위해 만든 deviceId를 공통으로 사용한다.
function getClientId() {
  let clientId = getCookie(COOKIE_DEVICE_ID_KEY);
  if (!clientId) {
    clientId = `KP.${(Math.random() * 4294967296) >>> 0}.${Date.now()}`;
  }
  // expire 갱신을 위해 항상 호출한다.
  setCookie({
    key: COOKIE_DEVICE_ID_KEY,
    value: clientId,
    expire: TWO_YEARS_BY_HOURS,
  });
  return clientId;
}

export function showAuthModal({
  titleType,
  pageInfoAfterClose,
  isSaveReturnPage = false,
  returnPageUrl,
}: {
  titleType?: IAuthModalTitleType;
  pageInfoAfterClose?: PageInfo;
  isSaveReturnPage?: boolean;
  returnPageUrl?: string;
} = {}) {
  isSaveReturnPage && saveReturnPage(returnPageUrl);
  getClientStore()?.dispatch(
    authActions.setValue('authModal', {
      show: true,
      titleType,
      pageInfoAfterClose,
    })
  );
}

export function hideAuthModal() {
  getClientStore()?.dispatch(
    authActions.setValue('authModal', {
      show: false,
    })
  );
}

export function getIsIdVerificationCompleted() {
  if (REGION === 'kor') {
    const store = getClientStore();
    const user = store?.getState().auth.user;
    return user?.isIdentityVerified;
  } else {
    return true;
  }
}

export function showIdVerificationModal(
  type: IIdVerificationModalType,
  pageInfoAfterClose?: PageInfo
) {
  const store = getClientStore();
  if (store && !store.getState().auth.idVerificationModal) {
    store.dispatch(
      authActions.setValue('idVerificationModal', {
        type,
        pageInfoAfterClose,
      })
    );
  }
}

export function getRegexNumber() {
  return /^[0-9]*$/;
}
export function getRegexEmail() {
  // eslint-disable-next-line no-useless-escape
  return /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
}

function getRegexStaticWeb() {
  return /(sandbox\/)*static-web\//g;
}

export async function sha256(str: string) {
  const data = convertStrToUint8Array(str);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return hash;
}

export async function decrypt({
  data,
  cryptoKey,
  iv,
}: {
  iv: ArrayBuffer;
  cryptoKey: CryptoKey;
  data: ArrayBuffer;
}) {
  return crypto.subtle
    .decrypt({name: 'AES-CBC', iv}, cryptoKey, data)
    .then(decrypted => decrypted)
    .catch(err => {
      console.error(`decrypt error: ${err}`);
    });
}

export async function importKey(rawKey: ArrayBuffer) {
  return crypto.subtle
    .importKey('raw', rawKey, {name: 'AES-CBC', length: 256}, false, [
      'decrypt',
    ])
    .catch(err => {
      console.error(`importKey error: ${err}`);
    });
}

export function convertStrToUint8Array(str: string) {
  return new Uint8Array(str.split('').map(char => char.charCodeAt(0)));
}

/**
 * 팝업이 있는 상태에서 다른 계정으로 로그인하면 팝업은 이전 계정으로 구매가 진행이 되기 때문에 팝업을 닫아야 한다.
 * 따라서 화면 전환 시에 팝업을 닫도록 처리한다
 */
let isCashPopupObserverAdded = false;
let cashOpener: Window | null = null;
function closeCashPopup() {
  if (cashOpener) {
    !cashOpener.closed && cashOpener.close();
    cashOpener = null;
  }
}
export function openCashPopup(pageInfo: PageInfo, onRefresh?: () => void) {
  if (!IS_CLIENT) return;

  if (
    pageInfo.page === Page.MoreCashPurchase ||
    pageInfo.page === Page.MoreAutoCashPurchase
  ) {
    window.refreshCashInfo = () => {
      const store = getClientStore();
      const user = store?.getState().auth.user;
      if (store && user) {
        if (onRefresh) {
          onRefresh();
        } else {
          store.dispatch(actions.fetchUserCashInfo());
        }
      }
    };
  }

  if (!isCashPopupObserverAdded) {
    isCashPopupObserverAdded = true;
    window.addEventListener('unload', closeCashPopup);
    addRouterObserver('routeChangeComplete', closeCashPopup);
  } else {
    closeCashPopup();
  }

  const url = getUrl(pageInfo);
  cashOpener = openWindow({
    url,
    windowName: url,
    features: 'width=375px,height=600px',
  });
}

export function getKakaoAppKey() {
  return IS_CBT_OR_PROD
    ? '48432be89b3a9cc4b1984569f22ed2cb'
    : IS_QA
      ? 'd3a5efb6f919b940fe4d00aeee5ead5c'
      : '6e059c26c15a92837ed6fa232a09e336';
}

export function getMetaTitle(title: string) {
  return title + ' | ' + SITE_NAME[REGION];
}

export function getWebviewProps({
  userAgent,
  query,
  allowRegions = ['kor', 'idn', 'tha', 'twn'],
  res,
}: {
  userAgent?: IUserAgent;
  query: {space?: string};
  allowRegions?: IRegion[];
  res?: ServerResponse;
}) {
  if (!allowRegions.includes(REGION) && res) {
    redirectTo({pageInfo: '/404', res});
    return {} as any;
  }

  return {
    isWebview: !!userAgent?.isWebView,
    space: query.space ? Number(query.space) : 0,
  };
}

export function getThemeOnBody() {
  return (document.body.dataset[THEME_DATASET_KEY] as ITheme) || DEFAULT_THEME;
}

export function getIsAdultBadge({label, imgType}: IBadge) {
  return label === '19' || imgType === 'adult';
}
